/**
 * The 2026 Display mockup expressed as a template document.
 *
 * This exists to prove the schema, not to be pretty. If the mockup cannot be
 * expressed declaratively then the schema is wrong and R2's builder would have been
 * built on sand — so this file is the design's acceptance test.
 */

import type { Template } from './template-schema.js';

export const DISPLAY_TEMPLATE: Template = {
  schemaVersion: 1,
  id: 'white-label-default-display',
  name: 'White Label Default — Display',
  version: 1,
  status: 'draft',
  agencyId: null,
  channels: ['DISPLAY'],
  lifecycle: ['PREFLIGHT', 'INTERIM', 'POSTFLIGHT'],

  theme: {
    color: {
      primary: '#001345',
      accent: '#06CDF2',
      surface: '#FFFFFF',
      surfaceMuted: '#F5F5F5',
      border: '#E8E8E8',
      textPrimary: '#0A0A0A',
      textInverse: '#FFFFFF',
      headerFrom: '#000A1E',
      headerTo: '#00174B',
      page: '#F1F3F6',
      stripBorder: '#2B5FA8',
    },
    // D34: Roboto, identified by measurement rather than substituted — 0.900 shape
    // IoU against the mockup versus 0.618 for the next-best candidate. Apache 2.0, so
    // server-side embedding needs no licence sign-off. This said 'Libre Franklin'
    // before the font was measured; the render service embeds Roboto, so leaving it
    // would have meant the theme naming one face while the PDF used another.
    // Base up (10→11) with a slightly tighter ratio (1.25→1.21): the body/label text reads a step
    // larger to match the sample, while the big KPI values (the top step) stay put — they were already
    // at mockup size, and growing them would risk overflowing the fixed summary page.
    typography: { fontFamily: 'Roboto', baseSizePt: 11, scale: 1.21 },
    page: { widthIn: 8.5, heightIn: 11, marginIn: 0 },
    branding: { companyName: 'ProData Media', copyrightName: 'ProData Media' },
    // A modest equal-height floor for the breakdown cards. Kept compact so the top row (Device Category
    // / Campaign Channels) does not pad out tall — the bottom row (Device Details / Browser Type) then
    // takes the content height it needs and the page keeps clear space before the footer.
    layout: { breakdownMinHeightIn: 1.55, pageBackground: 'waves', summaryRowSpaceIn: 0.06, detailsTopSpaceIn: 0.38 },
  },

  chrome: {
    header: [
      {
        id: 'hdr',
        type: 'PageHeader',
        props: { title: 'Campaign Evaluation Report', showAccentRule: true },
        bindings: { clientName: 'campaign.clientName', campaignId: 'campaign.reportSo', logo: 'presentation.logo', showLogo: 'presentation.showLogo' },
      },
    ],
    footer: [
      {
        id: 'ftr',
        type: 'PageFooter',
        props: {},
        // Year derived from the reporting period — never hardcoded, which is the
        // defect in all three mockups (2025 vs 2023, inconsistent casing).
        // The copyright names the AGENCY (D: reports carry the agency's copyright, not the client's).
        bindings: { companyName: 'campaign.agencyName', year: '$substring(report.periodEnd, 0, 4)' },
      },
    ],
  },

  pages: [
    // ------------------------------------------------------------- page 1 --
    {
      id: 'summary',
      mode: 'fixed',
      title: 'Campaign summary',
      blocks: [
        /*
         * Two-up: creative alongside campaign identity, as the mockup has it.
         *
         * This was a single stacked column until the pagination work added a real
         * fixed-page overflow check, which showed page 1 running 304px past the sheet.
         * `overflow: hidden` had been quietly clipping it since the walking skeleton —
         * the page looked plausible and was losing its last block.
         */
        {
          id: 'identity-row',
          type: 'Columns',
          props: { columns: 2 },
          bindings: {},
          children: [
            { id: 'creative', type: 'CreativePreview', props: { maxHeightIn: 1.9, bare: true }, bindings: { assetId: 'campaign.bannerAssetId' } },
            {
              id: 'meta',
              type: 'CampaignMetaCard',
              // showNote renders the editable "Report note" (campaign.notes) under the campaign details.
              props: { variant: 'icons', showNote: true },
              bindings: {
                clientName: 'campaign.clientName',
                startDate: 'campaign.startDate',
                endDate: 'campaign.endDate',
                campaignId: 'campaign.reportSo',
                note: 'campaign.notes',
              },
            },
          ],
        },
        {
          id: 'kpis',
          type: 'KpiTileRow',
          props: { columns: 4 },
          bindings: {
            tiles: `[
              {"label": "Targeted Impressions",  "value": overall.delivery.impressions,          "icon": "kpi-targeted-impressions"},
              {"label": "Total Website Clicks",  "value": overall.delivery.clicks,               "icon": "kpi-total-clicks"},
              {"label": "Retargeted Impressions","value": overall.delivery.retargetImpressions,  "icon": "kpi-retargeted-impressions"},
              {"label": "Retargeted Clicks",     "value": overall.delivery.retargetClicks,       "icon": "kpi-retargeted-clicks"}
            ]`,
          },
        },
        {
          id: 'delivery',
          type: 'DeliveryStrip',
          props: { ctrFootnote: 'CTR is calculated against contracted impressions.' },
          bindings: {
            contracted: 'overall.contract.targetImpressions',
            overDelivered: 'overall.derived.overDeliveredImpressions',
            overDeliveryPct: 'overall.derived.overDeliveryPct',
            ctr: 'overall.derived.ctr',
          },
        },
        // The mockup's 2x2 grid: device category + channels on top, device details + browser
        // type beneath — not three across with the details full-width below.
        {
          id: 'breakdown-top',
          type: 'Columns',
          props: { columns: 2 },
          bindings: {},
          children: [
            {
              id: 'device-cat',
              type: 'DeviceCategoryCard',
              // Monitor-and-phone illustration, sized so the top row stays compact (frees the page's
              // lower space for Device Details / Browser Type and the footer).
              props: { artHeightPx: 62 },
              bindings: { mobile: 'overall.delivery.mobileClicks', desktop: 'overall.delivery.desktopClicks' },
            },
            { id: 'channels', type: 'CampaignChannelsCard', props: { iconSize: 88 },
              bindings: { channels: 'campaign.channels', retargetClicks: 'overall.delivery.retargetClicks', retargetImpressions: 'overall.delivery.retargetImpressions' } },
          ],
        },
        {
          id: 'breakdown-bottom',
          type: 'Columns',
          props: { columns: 2 },
          bindings: {},
          children: [
            { id: 'device-detail', type: 'DeviceDetailsTable', props: { maxRows: 14, title: 'Mobile Device Details' }, bindings: { rows: 'overall.devices' } },
            { id: 'browsers', type: 'BrowserTypeCard', props: { maxItems: 5, imageLogos: true }, bindings: { rows: 'overall.browsers' } },
          ],
        },
        // Renders only when figures were adjusted (scope R2).
        { id: 'provenance', type: 'ProvenanceNote', props: {}, bindings: { show: 'provenance.hasAdjustedFigures' }, visibleWhen: 'provenance.hasAdjustedFigures' },
      ],
    },

    // ------------------------------------------------------------- page 2 --
    {
      id: 'targeting',
      mode: 'fixed',
      title: 'Targeting',
      // All three targeting sections are off by default, so the whole page hides unless at least one
      // is turned on — a checkbox in the drawer or a `*_targeting=Y` query param on the report URL.
      // Without this the page would print blank whenever every section is off.
      visibleWhen: 'presentation.showGeoTargeting = true or presentation.showAudienceTargeting = true or presentation.showTopSites = true',
      blocks: [
        {
          id: 'geo-card',
          type: 'SectionCard',
          props: { title: 'Geographic Targeting', icon: 'banner-geo-targeting' },
          bindings: {},
          // Reviewer-controlled (Settings → Geographic Targeting) and OFF by default. Shown only when
          // the flag is explicitly true — set by the checkbox or by `geographic_targeting=Y`.
          visibleWhen: 'presentation.showGeoTargeting = true',
          children: [
            {
              id: 'geo-map',
              type: 'GeoTargetingMap',
              // 4.2in — trimmed a little further from 4.575 to give the audience section room (review).
              props: { heightIn: 4.2 },
              bindings: { mapAssetId: 'targeting.mapAssetId', zips: 'targeting.zips', radiusMiles: 'targeting.radiusMiles' },
            },
          ],
        },
        {
          id: 'audience-card',
          type: 'SectionCard',
          props: { title: 'Audience Targeting', icon: 'banner-audience-targeting' },
          bindings: {},
          // Reviewer-controlled and OFF by default (checkbox or `audience_targeting=Y`). When on but
          // the campaign targets broadly (no notes), the card draws a muted "broad reach" line rather
          // than vanishing — the toggle decides presence, the data decides the contents.
          visibleWhen: 'presentation.showAudienceTargeting = true',
          children: [
            {
              id: 'audience', type: 'AudienceTargetingCard', props: {},
              bindings: { items: 'targeting.audience.notes' },
            },
          ],
        },
        {
          id: 'sites-card',
          type: 'SectionCard',
          props: { title: 'Top Sites', icon: 'banner-top-sites' },
          bindings: {},
          // Post-flight only (there are no delivered sites before a campaign runs), and
          // reviewer-controlled and OFF by default (checkbox or `top_sites=Y`). Delivery is not yet
          // consolidated by publisher, so `topSites` is currently empty and the card shows a muted
          // note; real sites appear once the data exists (docs/02-backlog.md).
          lifecycle: ['INTERIM', 'POSTFLIGHT'],
          visibleWhen: 'presentation.showTopSites = true',
          children: [
            { id: 'sites', type: 'TopSitesList', props: { columns: 2 }, bindings: { sites: 'targeting.topSites' } },
          ],
        },
      ],
    },

    // ------------------------------------------------------------- page 3 --
    // One page per ad group, flowing if the creative list is long.
    {
      id: 'details',
      mode: 'flowing',
      title: 'Campaign details',
      repeat: { over: 'adGroups', as: 'adGroup', emptyBehaviour: 'hide' },
      blocks: [
        {
          id: 'details-card',
          type: 'SectionCard',
          props: { title: 'Campaign Details', icon: 'banner-campaign-details' },
          // The ad group's name, right-aligned in the heading — "Ad Group: Display 1".
          bindings: { rightText: '"Ad Group: " & $adGroup.name' },
          children: [
            { id: 'creative-list', type: 'CreativeDetailList', props: { showClicks: true, maxHeightIn: 1.2 }, bindings: { creatives: '$adGroup.creatives' } },
          ],
        },
      ],
    },
  ],

  createdAt: '2026-08-15T00:00:00.000Z',
  createdBy: 'scoping',
};
